import React from 'react';

import type { Asset } from 'contentful';
import { Asset as AssetComponent, Props as AssetProps } from './Asset';

type BaseProps = AssetProps<HTMLVideoElement, React.VideoHTMLAttributes<HTMLVideoElement>>;

export type Props = Pick<BaseProps, Exclude<keyof BaseProps, 'poster' | 'controlsList'>> & {
  video?: Asset;
};

export class Video extends AssetComponent<HTMLVideoElement, Props> {
  get isMuted() {
    return (this.el && this.el.muted) || false;
  }
  set isMuted(muted: boolean) {
    if (this.el) {
      this.el.muted = muted;
    }
  }

  /* Needed to re-force iOS Safari autoplay.
   * iOS 10+ blocks autoplay for videos without the 'muted' prop but React strips
   * away this property for videos without audio seeing it as redundant.
   *
   * See https://github.com/facebook/react/issues/6544
   */
  updateMutedAutoplay() {
    if (this.el && this.props.autoPlay && this.props.muted) {
      this.el.setAttribute('muted', '');
      this.el.setAttribute('playsinline', '');
      this.el.setAttribute('autoplay', '');
    }
  }

  play() {
    if (!this.el || !this.el.play) return undefined;

    const p = this.el.play();

    if (p instanceof Promise) {
      // downgrade from error to warning
      return p.catch(err => window.console.warn(err));
    }
    return p;
  }

  pause() {
    if (this.el && this.el.pause) {
      this.el.pause();
    }
  }

  componentDidMount() {
    super.componentDidMount();
    this.updateMutedAutoplay();
  }

  componentDidUpdate() {
    this.updateMutedAutoplay();
  }

  render() {
    const {
      autoHeight,
      autoWidth,
      imageHeight,
      imageWidth,
      fit,
      focus,
      asset,
      video,
      children,
      format,
      png8,
      style,
      ...props
    } = this.props;

    const file = video && video.fields && video.fields.file;

    if (!file) {
      return null;
    }

    const videoUrl = file.url;
    const videoContentType = file.contentType;

    return (
      <video
        ref={this.elRef}
        style={{ ...style, backgroundImage: this.assetUrl }}
        poster={this.assetUrl}
        // chrome no download button
        controlsList="nodownload"
        {...props}
      >
        <source src={videoUrl} type={videoContentType} />
        {children}
      </video>
    );
  }
}
